import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DECLINE_TEMPLATE = "Thank you for reaching out to Patria's Pastoral Counseling Ministry. We were not able to confirm Growth Track completion or six months of regular attendance in our records yet. Growth Track is the fastest way forward, and we would love to see you there. In the meantime, our resources, Life Care classes, and prayer gatherings are open to you, and you can always reach out to us.";
const ATTEST = { growth_track: "Growth Track complete", attendance: "Six months of attendance", neither: "Neither yet" };

export const EligibilityActions = ({ kase, onDone }) => {
  const { user } = useAuth();
  const [mode, setMode] = useState(null);
  const [text, setText] = useState("");
  const run = useMutation({
    mutationFn: ({ action, body }) => api.post(`/cases/${kase.id}/eligibility/${action}`, body),
    onSuccess: () => { toast.success("Saved."); setMode(null); setText(""); onDone?.(); },
    onError: (e) => toast.error(apiError(e)),
  });
  const isDirector = user.roles.includes("care_director");
  return (
    <>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-primary min-h-[40px]" onClick={() => setMode("verify")} data-testid={`elig-verify-${kase.id}`}>Verify</button>
        <button type="button" className="btn-secondary min-h-[40px]" onClick={() => { setMode("exception"); setText(""); }} data-testid={`elig-exception-${kase.id}`}>{isDirector ? "Pastoral Exception" : "Request Exception"}</button>
        <button type="button" className="btn-quiet min-h-[40px]" onClick={() => { setMode("decline"); setText(DECLINE_TEMPLATE); }} data-testid={`elig-decline-${kase.id}`}>Decline</button>
      </div>
      <Dialog open={!!mode} onOpenChange={(o) => !o && setMode(null)}>
        <DialogContent className="bg-cream-50" data-testid="eligibility-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{mode === "verify" ? "Verify Eligibility" : mode === "exception" ? "Grant A Pastoral Exception" : "Decline With A Message"}</DialogTitle>
            <DialogDescription>{mode === "verify" ? "Confirms you checked Patria's records by hand." : mode === "exception" ? "A typed reason is required and is kept with the case." : "This message is emailed to the person as written."}</DialogDescription>
          </DialogHeader>
          <label htmlFor="elig-text" className="label">{mode === "verify" ? "Note (optional)" : mode === "exception" ? "Reason" : "Message"}</label>
          <textarea id="elig-text" className="input min-h-[120px]" value={text} onChange={(e) => setText(e.target.value)} data-testid="eligibility-text" />
          <div className="mt-4 flex gap-3">
            <button type="button" className="btn-primary" disabled={mode !== "verify" && text.trim().length < 5}
              onClick={() => run.mutate({ action: mode, body: mode === "verify" ? { note: text } : mode === "exception" ? { reason: text } : { message: text } })} data-testid="eligibility-confirm">Confirm</button>
            <button type="button" className="btn-secondary" onClick={() => setMode(null)}>Cancel</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function StaffIntake() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ["eligibility", "pending"], queryFn: async () => (await api.get("/cases/eligibility/pending")).data, refetchInterval: 60_000 });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["eligibility"] }); qc.invalidateQueries({ queryKey: ["staff"] }); qc.invalidateQueries({ queryKey: ["cases"] }); };
  return (
    <div data-testid="staff-intake">
      <h1 className="text-4xl">Intake & Eligibility</h1>
      <p className="mt-3 text-ink-500 max-w-[65ch]">Each new Care Intake waits here until you confirm eligibility against Patria's records by hand. Verification must be complete before matching.</p>
      {items.length === 0 && <p className="surface mt-8 max-w-xl p-6 text-ink-500" data-testid="eligibility-empty">Nothing to verify right now.</p>}
      <ul className="mt-8 divide-y divide-sage-400/50 border-y border-sage-400/50" data-testid="eligibility-list">
        {items.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center justify-between gap-4 py-5" data-testid={`eligibility-item-${c.id}`}>
            <div>
              <p className="font-serif text-xl font-semibold"><Link to={`/staff/cases/${c.id}`} className="hover:underline underline-offset-4">{c.display_name}</Link> <span className="text-ink-500 font-sans text-base">· {c.case_code}{c.restricted ? " · Restricted" : ""}</span></p>
              <p className="mt-1 text-sm text-ink-500">{c.masked ? "Restricted case" : `${ATTEST[c.eligibility_attestation] || c.eligibility_attestation} · ${c.campus || "no campus"} · ${c.care_for}`} · waiting {c.waiting_minutes < 60 ? `${c.waiting_minutes} min` : `${Math.floor(c.waiting_minutes / 60)} h`} · state {c.state.replace(/_/g, " ")}</p>
            </div>
            {!c.masked && <EligibilityActions kase={c} onDone={refresh} />}
          </li>
        ))}
      </ul>
    </div>
  );
}
